/**
 * Card Product Variant Selector
 *
 * Handles variant selection within product cards to update product variant ID
 * for quick-add forms and image display.
 * Uses custom events to notify other components of variant changes.
 */
class ProductCardVariantSelector extends HTMLElement {
  constructor() {
    super();
  }

  // This lifecycle method is called when the element is added to the DOM.
  // We use it for initialization as well as adding a dispatch event so that
  // we can properly keep the product cards in sync if a customer navigates back
  // to a page where they had selected a product variant.
  connectedCallback() {
    this.variantSelect = this.querySelector('select');
    this.productId = this.getAttribute('data-product-id');
    this.sectionId = this.getAttribute('data-section-id');
    this.variantData = this.getVariantData();

    if (this.variantSelect) {
      this.variantSelect.addEventListener('change', this.onVariantChange.bind(this));

      // If document is already loaded, dispatch immediately
      if (document.readyState === 'complete') {
        if (this.variantSelect.value) {
          this.dispatchVariantChangedEvent(this.variantSelect);
        }
      } else {
        // Otherwise wait for load event (auto-removes via once:true)
        window.addEventListener(
          'load',
          () => {
            if (this.variantSelect.value) {
              this.dispatchVariantChangedEvent(this.variantSelect);
            }
          },
          { once: true },
        );
      }
    }
  }

  getVariantData() {
    let variantDataElement = this.querySelector('[data-variant-data]');
    return variantDataElement ? JSON.parse(variantDataElement.textContent) : [];
  }

  onVariantChange(event) {
    this.dispatchVariantChangedEvent(event.target);
  }

  dispatchVariantChangedEvent(selectElement) {
    const variantId = selectElement.value;
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    const isAvailable = !selectedOption.hasAttribute('disabled');
    const variant = this.variantData.find((v) => v.id.toString() === variantId.toString());

    // Dispatch event for other components to react to the variant change
    document.dispatchEvent(
      new CustomEvent('variant:changed', {
        bubbles: true,
        detail: {
          variantId: variantId,
          productId: this.productId,
          sectionId: this.sectionId,
          isAvailable: isAvailable,
          variant: variant,
        },
      }),
    );
  }
}
customElements.define('product-card-variant-selector', ProductCardVariantSelector);
